import { useId, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { t } from "ttag";

import { setLevelsRxPassword } from "@/app/api/legacy-api";
import { PasswordTips } from "@/app/components/legacy-components";
import { usePortalUserStore } from "@/app/stores";
import { confirmPasswordSchema } from "@/shared/models/legacy-schemas";
import {
  Button,
  Dialog,
  errorToast,
  Form,
  PasswordInput,
  WarningCircleIcon,
} from "@/shared/ui";
import { handleAxiosFieldErrors } from "@/shared/utils";

export const SetUpPasswordAlertModal = () => {
  const portalUser = usePortalUserStore((state) => state.portalUser);

  const formId = useId();

  const [show, setShow] = useState(Boolean(portalUser?.needsChangePassword));

  const form = useForm({
    resolver: zodResolver(confirmPasswordSchema),
  });

  const queryClient = useQueryClient();

  const { mutate: setLevelsRxPasswordMutation, isPending } = useMutation({
    mutationFn: setLevelsRxPassword.mutation,
    onSuccess: () => {
      setLevelsRxPassword.invalidates(queryClient);
      setShow(false);
    },
    onError: (error) => {
      handleAxiosFieldErrors(error, form.setError);
      errorToast(error);
    },
  });

  const handleSubmit = form.handleSubmit((values) =>
    setLevelsRxPasswordMutation(values),
  );

  return (
    <Dialog.Root open={show} alert>
      <Dialog.Content withCloseButton={false}>
        <Dialog.Header icon={WarningCircleIcon}>
          <Dialog.Title>{t`Please, confirm your password`}</Dialog.Title>
          <Dialog.Description>
            {t`At LevelsRx, protecting your information is a top priority.`}
            <br />
            {t`We have added an additional layer of authentication to our log in.`}
            <br />
            {t`Please create and verify a new password.`}
          </Dialog.Description>
        </Dialog.Header>

        <Form.Provider {...form}>
          <form id={formId} onSubmit={handleSubmit}>
            <Form.Field
              control={form.control}
              name="password"
              render={() => (
                <Form.Item>
                  <Form.Label aria-required>{t`Password`}</Form.Label>
                  <Form.Control>
                    <PasswordInput
                      {...form.register("password")}
                      placeholder={t`Password`}
                      autoComplete="new-password"
                    />
                  </Form.Control>
                  <Form.Message compact={false} />
                </Form.Item>
              )}
            />

            <Form.Field
              control={form.control}
              name="passwordConfirmation"
              render={() => (
                <Form.Item>
                  <Form.Label aria-required>{t`Confirm password`}</Form.Label>
                  <Form.Control>
                    <PasswordInput
                      {...form.register("passwordConfirmation")}
                      placeholder={t`Confirm password`}
                      autoComplete="new-password"
                    />
                  </Form.Control>
                  <Form.Message compact={false} />
                </Form.Item>
              )}
            />

            <PasswordTips />
          </form>

          <Dialog.Footer>
            <Button form={formId} type="submit" size="lg" disabled={isPending}>
              {t`Save`}
            </Button>
          </Dialog.Footer>
        </Form.Provider>
      </Dialog.Content>
    </Dialog.Root>
  );
};
